import { EmojiClickData, SkinTones } from "emoji-picker-react";

/** Returns true if the string is a unified hex code (e.g. "1f389" or "1f468-200d-1f4bb") */
export const isUnifiedHex = (str: string): boolean =>
  /^[0-9a-fA-F]+(-[0-9a-fA-F]+)*$/.test(str);

/**
 * Converts an emoji to a unified hex code for emoji-picker-react.
 * Accepts both native characters and hex codes.
 * Handles compound emojis with multiple code points (e.g. ZWJ sequences).
 */
export const toUni = function (str: string): string {
  if (!str) return "1f389";
  // Already a unified hex code, pass through
  if (isUnifiedHex(str)) return str;
  // Convert native emoji to unified hex
  const codePoints: string[] = [];
  for (const char of str) {
    const cp = char.codePointAt(0);
    if (cp !== undefined) {
      codePoints.push(cp.toString(16));
    }
  }
  return codePoints.length > 0 ? codePoints.join("-") : "1f389";
};

/**
 * Converts a unified hex code to a native emoji string.
 * Accepts both hex codes and native characters.
 * Handles compound emojis with dash-separated code points.
 */
export const toNative = function (str: string): string {
  if (!str) return "";
  // Already a native emoji, pass through
  if (!isUnifiedHex(str)) return str;
  try {
    return str
      .split("-")
      .map((hex) => String.fromCodePoint(parseInt(hex, 16)))
      .join("");
  } catch {
    return "\u{1f389}";
  }
};

/**
 * Unified result type for emoji picker selections.
 *
 * Note on field stability:
 * - `unified` and `native` are stable identifiers - use these for persistence/comparison
 * - `id` and `colons` are derived from display names (e.g., "grinning_face") not shortcodes,
 *   so they may differ from older emoji-picker-element values. Avoid using for persistence.
 * - `emoticons` is always empty as emoji-picker-react doesn't expose this data
 */
export interface EmojiPickerResult {
  native: string;
  name: string;
  /** Stable identifier - use this for persistence and comparison */
  unified: string;
  /** Derived from display name, not stable across picker implementations */
  id: string;
  /** Derived from display name, not stable across picker implementations */
  colons: string;
  short_names: string[];
  skin: number | null;
  /** Always empty - emoji-picker-react doesn't expose emoticon data */
  emoticons: string[];
}

/** Map skin tone enum to numeric value */
function skinToneToNumber(skinTone: SkinTones): number | null {
  const mapping: Record<SkinTones, number | null> = {
    [SkinTones.NEUTRAL]: null,
    [SkinTones.LIGHT]: 1,
    [SkinTones.MEDIUM_LIGHT]: 2,
    [SkinTones.MEDIUM]: 3,
    [SkinTones.MEDIUM_DARK]: 4,
    [SkinTones.DARK]: 5,
  };
  return mapping[skinTone] ?? null;
}

/** Convert an emoji-picker-react EmojiClickData into a normalized EmojiPickerResult */
export function convertEmojiClickData(data: EmojiClickData): EmojiPickerResult {
  const primaryName = data.names[0] || "emoji";
  const id = primaryName.toLowerCase().replace(/\s+/g, "_");

  return {
    native: data.emoji,
    name: primaryName,
    unified: data.unified,
    id,
    colons: `:${id}:`,
    short_names: data.names.map((n) => n.toLowerCase().replace(/\s+/g, "_")),
    skin: skinToneToNumber(data.activeSkinTone),
    emoticons: [],
  };
}

// Re-export types from emoji-picker-react for convenience
export type { EmojiClickData } from "emoji-picker-react";
